import { inject } from '@angular/core';
import { Router, CanActivateFn, CanActivateChildFn } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Admin Guard (Functional Guard)
 *
 * Protects routes by checking if user is in the required Cognito group.
 * The required group is defined in environment.cognitoGroupName.
 *
 * - Development: Requires "Admin" group
 * - Staging/Production: Requires "Users" group (or configured value)
 *
 * Redirects to /unauthorized if user lacks required group membership.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'users',
 *   canActivate: [authGuard, adminGuard],
 *   component: UsersListComponent
 * }
 * ```
 */
export const adminGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.getUserGroups().pipe(
    map((groups) => {
      const requiredGroup = environment.cognitoGroupName;
      const hasRequiredGroup = groups.includes(requiredGroup);

      if (hasRequiredGroup) {
        return true;
      } else {
        console.warn(
          `User does not have required group: ${requiredGroup}. User groups:`,
          groups
        );
        router.navigate(['/unauthorized']);
        return false;
      }
    }),
    catchError((err) => {
      console.error('Admin guard error:', err);
      router.navigate(['/unauthorized']);
      return of(false);
    })
  );
};

/**
 * Admin Guard for Child Routes (Functional Guard)
 *
 * Protects child routes by checking if user is in the required Cognito group.
 * Redirects to /unauthorized if user lacks required group membership.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'users',
 *   canActivateChild: [authGuardChild, adminGuardChild],
 *   loadChildren: () => import('./features/users/routes').then(m => m.routes)
 * }
 * ```
 */
export const adminGuardChild: CanActivateChildFn = (route, state) => {
  return adminGuard(route, state);
};
